import { motion, useAnimate } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../shared/components/Button';
import { ButtonsGroup } from '../../shared/components/ButtonsGroup';
import { OllieFloating } from '../../shared/components/OllieFloating';
import { SpeechBubble } from '../../shared/components/SpeechBubble';
import { ROUTES } from '../../shared/routing/routes';
import { PersonalizingProgressDisplay } from './components/PersonalizingProgressDisplay';
import { useOnboardingOcdCycleStore } from './hooks/useOnboardingOcdCycleStore';
import { useOnboardingStore } from './hooks/useOnboardingStore';

const progressMessages = [
    "Ollie's reading your answers...",
    "Adding your OCD Themes...",
    "Ollie's noticing your OCD Patterns...",
    "Helping Ollie learn the details...",
    "Ollie's almost ready to help..."
];

const listItems = [
    { text: "Learned about your OCD Themes", isVisible: false },
    { text: "Taught Ollie about you", isVisible: false },
    { text: "Sipped some tea :)", isVisible: false },
    { text: "Personalized app experience", isVisible: false }
];

export const PersonalizingOllie = () => {
    const navigate = useNavigate();
    const setCurrentStep = useOnboardingStore(state => state.setCurrentStep);
    const generateOcdCycleSteps = useOnboardingOcdCycleStore((state) => state.generateOcdCycleSteps);
    const generateOcdCycleStepsPromiseRef = useRef<Promise<void> | null>(null);

    const [scope, animate] = useAnimate();

    const [layoutState, setLayoutState] = useState<"loading" | "ready">("loading");

    useEffect(() => {
        setCurrentStep(undefined); // hide the progress bar
        if (!generateOcdCycleStepsPromiseRef.current) {
            generateOcdCycleStepsPromiseRef.current = Promise.all([
                generateOcdCycleSteps(),
            ]).then(() => undefined);
        }
    }, [setCurrentStep, generateOcdCycleSteps]);

    useEffect(() => {
        const handleReadyAnimation = async () => {
            await animate("#pageTitle2", { opacity: 1 });
        }
        if (layoutState === "ready") {
            handleReadyAnimation();
        }
    }, [layoutState]);

    const handleBeforeAnimationComplete = async () => {
        await generateOcdCycleStepsPromiseRef.current;
    }

    const handleAnimationComplete = async () => {
        await animate("#pageTitle1", { opacity: 0 }, { duration: 0.5 });
        setLayoutState("ready");
    }

    const handleContinue = async () => {
        navigate(ROUTES.onboarding.register);
    }

    return (
        <div ref={scope} className='flex flex-col gap-10 safe-pb-4 px-8 flex-1 overflow-hidden'>
            <title>Personalizing Ollie</title>
            <div className='flex-shrink-0'>
                {layoutState === "loading" && <h1 id='pageTitle1' className='h-[64px] text-2xl font-semibold text-center'>Personalizing Ollie for You</h1>}
                {layoutState === "ready" && <h1 id='pageTitle2' className='h-[64px] text-2xl font-semibold text-center opacity-0'>All set!<br />Ollie's ready to help you!</h1>}
            </div>
            <div className='flex-1 flex flex-col justify-center overflow-hidden'>
                <div id="ollieFloatingContainer" className='flex flex-col justify-end'>
                    {layoutState === "ready" &&
                        <motion.div layout className='flex justify-center'>
                            <SpeechBubble text="Thanks for sharing! Let's work together towards finding Relief from OCD" animationDelay={0.3} />
                        </motion.div>
                    }
                    <motion.div layout transition={{
                        type: "spring" as const,
                        damping: 18,
                        stiffness: 70,
                        mass: 3
                    }} className='w-full flex justify-center items-center'>
                        <OllieFloating />
                    </motion.div>
                </div>
                {layoutState === "loading" && <PersonalizingProgressDisplay
                    title="Setting up your first OCD insight"
                    progressMessages={progressMessages}
                    listItems={listItems}
                    onAnimationComplete={handleAnimationComplete}
                    beforeAnimationComplete={handleBeforeAnimationComplete}
                />
                }
            </div>
            {layoutState === "ready" && <motion.div id="buttonsContainer" className="flex flex-col items-center gap-4 flex-shrink-0" >
                <div className="flex flex-col items-center gap-1 w-full">
                </div>
                <ButtonsGroup>
                    <Button
                        onClick={handleContinue}
                        label="I'm ready"
                    />
                </ButtonsGroup>
            </motion.div>}
        </div>
    );
};